import { useState, useMemo, useEffect, ChangeEvent, useCallback } from 'react';
import { twJoin } from 'tailwind-merge';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

import IconButton from '@/components/ui/custom/buttons/IconButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import TextAreaInput from '@/components/ui/custom/dataInput/TextAreaInput';
import { ModularCard } from '@/components/ui/custom/modular/ModularCard';
import useToggle from '@/data/hook/useToggle';
import { Section } from '@/models/Book';
import SectionIcon from '../sections/view/SectionIcon';
import Text from '@/components/ui/custom/dataDisplay/Text';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';

type InstructionProps = {
  section: Section;
  readOnly?: boolean;
  loading?: boolean;
  handleUpdate(sectionId: number, instructions: string | null): void;
  handleRemove(sectionId: number): Promise<void>;
};

export default function Instruction(props: InstructionProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { section, readOnly, loading, handleRemove, handleUpdate } = props;

  const { isOpen, toggle, open } = useToggle(section.editing);

  const [editing, setEditing] = useState(section.editing);

  const [value, setValue] = useState(section.instructions);

  const [changed, setChanged] = useState(false);

  const [errorLabel, setErrorLabel] = useState<string>();

  const [openModal, setOpenModal] = useState(false);

  const updateInstructions = useCallback(
    (instructions: string | null) => {
      if (!instructions) {
        setChanged(false);
        return;
      }

      handleUpdate(section.id, instructions);
      setChanged(false);
    },
    [handleUpdate, section.id],
  );

  const debouncedUpdate = useMemo(
    () => debounce(updateInstructions, 500),
    [updateInstructions],
  );

  useEffect(() => {
    if (changed) debouncedUpdate(value);

    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate, value, changed]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const instructions = e.target.value;

    if (!instructions) {
      setErrorLabel(
        t('form.required', {
          field: t('instructions').toLowerCase(),
        }),
      );
    } else {
      setErrorLabel(undefined);
    }

    setValue(instructions);
    setChanged(true);
  };

  const handleEdit = () => {
    open();
    setEditing(prev => !prev);
    setValue(section.instructions);
    setErrorLabel(undefined);
  };

  return (
    <li
      className={twJoin(
        'flex flex-col gap-2.5 border-l border-primary-content rounded-t-md',
        loading ? 'disabled' : '',
        isOpen ? '' : 'rounded-b-md',
      )}
    >
      <ModularCard.Header
        isOpen={isOpen}
        onOpen={() => {
          if (isOpen) {
            setEditing(false);
          }
          toggle();
        }}
        className={{
          base: 'p-0',
          section:
            'flex-row-reverse bg-primary-content rounded-md font-500 py-1 px-2.5 max-w-[70%]',
          button: 'right-0',
        }}
        title={section.title}
        icon={<SectionIcon icon={section.icon} type="outline" />}
      >
        <ConditionalRenderer condition={!readOnly}>
          <aside className="flex gap-2">
            <Tooltip text={t('edit')}>
              <IconButton
                testId="editButton"
                icon={
                  <PencilIcon className="w-4 text-primary transition-all duration-150 ease-in-out hover:text-primary/50" />
                }
                onClick={e => {
                  e.stopPropagation();
                  handleEdit();
                }}
              />
            </Tooltip>

            <Tooltip text={t('remove')}>
              <IconButton
                testId="removeButton"
                icon={<TrashIcon />}
                className="w-4 text-error transition-all duration-150 ease-in-out hover:text-error/50"
                onClick={e => {
                  e.stopPropagation();
                  setOpenModal(true);
                }}
              />
            </Tooltip>
          </aside>
        </ConditionalRenderer>
      </ModularCard.Header>
      <ModularCard.Accordion isOpen={isOpen} className={{ base: 'pl-1.5' }}>
        <ConditionalRenderer
          condition={editing && !readOnly}
          fallback={
            <Text text={section.instructions} className="min-h-[1.25rem]" />
          }
        >
          <TextAreaInput
            testId="instructionsTextArea"
            onChange={handleChange}
            value={value || ''}
            errorLabelText={errorLabel}
          />
        </ConditionalRenderer>
      </ModularCard.Accordion>
      <ModalDisable
        modalType="delete"
        onClickConfirm={async () => {
          await handleRemove(section.id);
          setOpenModal(false);
        }}
        visible={openModal}
        translationString="deleteSectionInstructions"
        onClickCancel={() => setOpenModal(false)}
        isRequesting={loading}
      />
    </li>
  );
}
